import { IonIcon } from '@ionic/react'
import { documentText, informationCircleOutline } from 'ionicons/icons'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { useInfoBox } from '../../../components/infoBox/useInfoBox'
import { ConsumptionDataContext } from '../../../contexts/ConsumptionDataContext'
import { ModalContext } from '../../../contexts/ModalContext'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../modules/trpc'
import OverViewCard from './OverviewCard'
import { useDayAheadPrices } from '../../../hooks/dayAheadPrices/useDayAheadPrices'
import { meanAvg } from '../../../utils/math'
import { priceString } from '../../../utils/priceUtils'

const PriceComparisonCard = () => {
	const object = useContext(ObjectContext)
	const { insertModal } = useContext(ModalContext)
	const { currentMonthAvgPricePerConsumption, lastAvailableConsumptionData } =
		useContext(ConsumptionDataContext)

	const { t } = useTranslation('pages_overview', { keyPrefix: 'priceComparisonCard' })

	const [consumptionEffect, setConsumptionEffect] = useState(0)
	const infoAnchorRef = useRef<HTMLIonIconElement>(null)

	const timestamp = lastAvailableConsumptionData ?? dayjs()
	const { dayAheadPrices } = useDayAheadPrices({
		from: timestamp.startOf('month'),
		to: timestamp.endOf('day'),
	})
	const dayAheadMeanPrice = meanAvg(dayAheadPrices.map(price => price.centsPerKwh))

	const { open, InfoBoxComponent } = useInfoBox<HTMLIonIconElement>()

	const { data: vaeComparisonSpotContractAccumulation } =
		trpc.objects.getInvoiceAccumulation.useQuery(
			{
				objectId: object.id,
				from: lastAvailableConsumptionData?.startOf('month').format() ?? '',
				to: lastAvailableConsumptionData?.endOf('day').format() ?? '',
				isSpotMargin: true,
				price: 0.4,
				groupBy: 'month',
			},
			{ enabled: Boolean(lastAvailableConsumptionData) }
		)


	const getValues = async () => {
		if (!lastAvailableConsumptionData) return { avgPrice: 0, benefit: 0 }
		const marginal = object.price.value

		const newConsumptionEffect = (currentMonthAvgPricePerConsumption ?? 0) - dayAheadMeanPrice - marginal
		setConsumptionEffect(newConsumptionEffect)
	}

	useEffect(() => {
		getValues()
	}, [
		object.price.value,
		lastAvailableConsumptionData,
		currentMonthAvgPricePerConsumption,
		dayAheadPrices,
	])

	const contractSwapBenefit = useMemo(() => {
		if (!currentMonthAvgPricePerConsumption || !vaeComparisonSpotContractAccumulation)
			return 0
		return currentMonthAvgPricePerConsumption
			- (vaeComparisonSpotContractAccumulation[0].avgPricePerConsumption ?? 0)
	}, [vaeComparisonSpotContractAccumulation, currentMonthAvgPricePerConsumption])

	const onInfoClick = () =>
		open([
			{
				content: <div style={{ padding: '0.5rem' }}>{t('porssisopimus')}</div>,
				anchor: infoAnchorRef,
			},
		])

	return (
		<>
			<OverViewCard
				headerText={t('otsikko')}
				icon={documentText}
				className='comparisoncard'
				disableChevron={false}
			>
				<div className='card-content'>
					{object.price.isSpotMargin ? (
						<div>
							<div className='texts'>
								<div className='text-text-dark dark:text-white'> {t('keskihinta')} </div>
								<div className='info border border-brand-primary-100-light dark:border-brand-primary-100-dark'>
									<div className='left'>
										<div className='value text-brand-primary-100-light dark:text-brand-primary-100-dark'>
											{priceString(
												(currentMonthAvgPricePerConsumption ?? 0) - (object.price.spotMargin ?? 0)
											)}{' '}
										</div>
										<div className='marginal text-text-dark dark:text-white'>
											+{object.price.spotMargin} snt/kWh
										</div>
									</div>
									<div className='comparison-card-icon'>
										<IonIcon
											className='button-hover text-text-dark dark:text-white'
											icon={informationCircleOutline}
											ref={infoAnchorRef}
											onClick={onInfoClick}
										/>
									</div>
								</div>
							</div>
							<div className='box-container'>
								<div className='box bg-background-100-light text-text-dark dark:bg-surface-dark dark:text-white'>
									{t('keskiarvo')}: <span>{priceString(dayAheadMeanPrice)} snt/kWh</span>
								</div>
								<div className='box bg-background-100-light text-text-dark dark:bg-surface-dark dark:text-white font-medium'>
									{t('kulutusvaikutuksesi')}:
									<span
										className={
											consumptionEffect > 0
												? 'text-highlight'
												: 'text-brand-primary-100-light dark:text-brand-primary-100-dark'
										}
									>
										<span className='font-bold'>{priceString(consumptionEffect)} snt/kWh</span>
									</span>
								</div>
							</div>
						</div>
					) : (
						<>
							<div className='texts'>
								<div className='text-text-dark dark:text-white'> {t('kiinteaHinta')} </div>
								<div className='info border border-brand-primary-100-light dark:border-brand-primary-100-dark'>
									<div className='value text-brand-primary-100-light dark:text-brand-primary-100-dark'>
										{priceString(object.price.value)}{' '}
									</div>
									<div className='marginal text-text-dark dark:text-white'> snt/kWh </div>
								</div>
							</div>
							{contractSwapBenefit > 0 && object.useDatahub && !object.synerallId && (
								<div className='suggestion rounded-xl p-4 border border-brand-primary-100-light dark:border-brand-primary-100-dark'>
									<div className='text'>
										<span className='text-text-dark dark:text-white'>
											{t('saasto')}
											<span className='font-semibold tracking-wide text-brand-primary-100-light dark:text-brand-primary-100-dark'>
												{` ${priceString(contractSwapBenefit)} `}
											</span>
											snt/kWh?
										</span>
									</div>
									<IonIcon
										onClick={() => insertModal('contractComparison')}
										className='info button-hover text-brand-primary-100-light dark:text-brand-primary-100-dark'
										icon={informationCircleOutline}
									/>
								</div>
							)}
						</>
					)}
				</div>
			</OverViewCard>

			{InfoBoxComponent}
		</>
	)
}

export default PriceComparisonCard
